import { useEffect, useMemo, useState, type ReactNode } from "react";
import { copy } from "../copy";
import { useBeatPlayer } from "../audio";
import { useSession } from "../session";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import Icon, { type IconName } from "../components/Icon";
import SiteFooter from "../components/SiteFooter";
import ContestCard from "../contests/ContestCard";
import FilterRail from "../contests/FilterRail";
import { useContests } from "../contests/data";
import { DEFAULT_FILTERS, countActiveFilters, filterContests, filterOptions, type ContestFilters } from "../contests/filters";

const button =
  "bf-focusable mt-6 inline-flex h-11 cursor-pointer items-center whitespace-nowrap rounded-md bg-money px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep";

function Notice({
  testId,
  icon,
  heading,
  body,
  action,
}: {
  testId: string;
  icon: IconName;
  heading: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-ground-edge bg-ground-raised p-8 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ground">
        <Icon name={icon} className="h-5 w-5 text-chrome-dim" />
      </div>
      <h2 className="mt-4 font-headline text-2xl uppercase leading-none text-chrome">{heading}</h2>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-chrome-dim">{body}</p>
      {action}
    </div>
  );
}

// The contests board. One read of /v1/web/contests; the rail filters that
// list in the browser, and nothing renders in place of contests that are
// not there.
export default function Contests() {
  const { state, retry } = useContests();
  const session = useSession();
  const member = session.kind !== "visitor";
  const reducedMotion = usePrefersReducedMotion();
  const player = useBeatPlayer();
  const [filters, setFilters] = useState<ContestFilters>(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const c = copy.contests;

  useEffect(() => {
    document.title = c.title;
  }, [c.title]);

  const contests = useMemo(() => (state.status === "ready" ? state.contests : []), [state]);
  const options = useMemo(() => filterOptions(contests), [contests]);
  // My contests is a member filter; a visitor's view never applies it.
  const applied = useMemo(() => (member ? filters : { ...filters, myContests: false }), [filters, member]);
  const results = useMemo(() => filterContests(contests, applied), [contests, applied]);
  const activeCount = countActiveFilters(applied);

  const update = <K extends keyof ContestFilters>(key: K, value: ContestFilters[K]) =>
    setFilters((cur) => ({ ...cur, [key]: value }));
  const reset = () => setFilters(DEFAULT_FILTERS);

  let body: ReactNode = null;
  if (state.status === "error") {
    body = (
      <Notice
        testId="contests-error"
        icon="errorWarning"
        heading={c.error}
        body={c.errorSub}
        action={
          <button type="button" onClick={retry} className={button}>
            {c.retry}
          </button>
        }
      />
    );
  } else if (state.status === "ready" && contests.length === 0) {
    body = <Notice testId="contests-empty" icon="trophy" heading={c.empty} body={c.emptySub} />;
  } else if (state.status === "ready" && results.length === 0) {
    body = (
      <Notice
        testId="contests-no-match"
        icon="searchEye"
        heading={c.noMatch}
        body={c.noMatchSub}
        action={
          <button type="button" onClick={reset} className={button}>
            {c.resetFilters}
          </button>
        }
      />
    );
  } else if (state.status === "ready") {
    body = (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {results.map((contest) => (
          <ContestCard key={contest.slug} contest={contest} player={player} reducedMotion={reducedMotion} />
        ))}
      </div>
    );
  }

  const ready = state.status === "ready" && contests.length > 0;

  return (
    <div className="min-h-screen overflow-x-hidden bg-ground font-sans text-chrome antialiased">
      <main className="px-4 pb-20 pt-24 md:px-6 md:pb-28 md:pt-32">
        <div className="mx-auto w-full max-w-[1240px]">
          <header className="border-b border-ground-edge pb-8 md:pb-10">
            <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{c.eyebrow}</p>
            <h1 className="mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome md:text-6xl">
              {c.heading[0]} <span className="text-gold">{c.heading[1]}</span>
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-chrome-dim">{c.sub}</p>
          </header>

          <div className={`mt-8 grid grid-cols-1 gap-8 ${ready ? "lg:grid-cols-[300px_1fr] lg:gap-10" : ""}`}>
            {ready && (
              <div className="min-w-0 lg:sticky lg:top-24 lg:h-fit">
                <button
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  aria-expanded={showFilters}
                  className="bf-focusable mb-4 flex h-11 w-full cursor-pointer items-center justify-between rounded-md border border-ground-edge bg-ground-raised px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-chrome lg:hidden"
                >
                  <span className="flex items-center gap-2">
                    <Icon name="filter" className="h-4 w-4 text-gold" />
                    {c.filters}
                    {activeCount > 0 && <span className="text-gold">({activeCount})</span>}
                  </span>
                  <Icon name={showFilters ? "arrowUp" : "arrowDown"} className="h-4 w-4 text-chrome-dim" />
                </button>
                <div className={`${showFilters ? "block" : "hidden"} lg:block`}>
                  <FilterRail
                    filters={applied}
                    options={options}
                    resultCount={results.length}
                    activeCount={activeCount}
                    member={member}
                    onChange={update}
                    onReset={reset}
                    onSearch={() => setShowFilters(false)}
                  />
                </div>
              </div>
            )}

            <section aria-label={c.results} className="min-w-0">
              {ready && (
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <p data-testid="contests-count" className="bf-tabular text-[11px] uppercase tracking-[0.18em] text-chrome-dim">
                    {c.found(results.length)}
                  </p>
                  {activeCount > 0 && (
                    <button
                      type="button"
                      onClick={reset}
                      className="bf-focusable cursor-pointer whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.14em] text-money transition-colors hover:text-money-deep"
                    >
                      {c.clear}
                    </button>
                  )}
                </div>
              )}
              {body}
            </section>
          </div>
        </div>
      </main>
      {player.elements}
      <SiteFooter />
    </div>
  );
}
